import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

interface School {
  name: string;
  type: string;
  notes?: string;
}

export default function SchoolsPage() {
  const { locale } = useLanguage();

  const highSchools: School[] = [
    { name: 'University High School', type: locale === 'zh' ? '高中' : 'High School', notes: locale === 'zh' ? '提供新生入学指南、华人家长会(PTA)信息、学校动态、学生风采' : 'New student guides, Chinese PTA, school updates, student spotlights' },
    { name: 'Northwood High School', type: locale === 'zh' ? '高中' : 'High School', notes: locale === 'zh' ? '提供新生入学指南、AP课程选择指导、学校动态' : 'New student guides, AP course selection guidance, school updates' },
    { name: 'Irvine High School', type: locale === 'zh' ? '高中' : 'High School', notes: locale === 'zh' ? '华人家长会欢迎会、多元文化节、学校动态' : 'Chinese parent association, multicultural festivals, school updates' },
  ];

  const middleSchools: School[] = [
    { name: 'Rancho San Joaquin Middle School', type: locale === 'zh' ? '初中' : 'Middle School', notes: locale === 'zh' ? '校园动态、课程信息' : 'Campus updates, course information' },
    { name: 'Sierra Vista Middle School', type: locale === 'zh' ? '初中' : 'Middle School' },
    { name: 'South Lake Middle School', type: locale === 'zh' ? '初中' : 'Middle School' },
    { name: 'Venado Middle School', type: locale === 'zh' ? '初中' : 'Middle School' },
  ];

  const resources = locale === 'zh' ? [
    { title: '新生入学指南', desc: '为每所高中提供详细的新生入学指南，帮助华人学生和家庭顺利过渡' },
    { title: 'AP课程选课指导', desc: '关于AP课程选择和大学准备的建议' },
    { title: 'ACT/SAT考试日程', desc: '标准化考试的日期和备考资源' },
    { title: '华人家长会 (PTA)', desc: '各学校的华人家长组织，提供新闻、活动和支持' },
    { title: '学校动态', desc: '定期更新的校园新闻和活动信息' },
    { title: '学生风采', desc: '展示华人学生的成就，如科学奥林匹克竞赛获奖等' },
  ] : [
    { title: 'New Student Guides', desc: 'Detailed orientation guides for each high school to help Chinese students and families transition smoothly' },
    { title: 'AP Course Selection', desc: 'Guidance on AP course selection and college preparation' },
    { title: 'ACT/SAT Test Dates', desc: 'Standardized test dates and preparation resources' },
    { title: 'Chinese PTA', desc: 'Chinese parent organizations at each school providing news, events, and support' },
    { title: 'School Updates', desc: 'Regularly updated campus news and activity information' },
    { title: 'Student Spotlights', desc: 'Showcasing Chinese student achievements, including Science Olympiad state championships' },
  ];

  const sectionStyle: React.CSSProperties = { marginBottom: 48 };
  const schoolCardStyle: React.CSSProperties = {
    padding: '20px 24px',
    background: 'var(--white)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-sm)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
  };

  return (
    <div>
      {/* Hero */}
      <section
        style={{
          background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
          color: '#fff',
          padding: '100px 0 60px',
        }}
      >
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '2.8rem', fontWeight: 900, marginBottom: 12 }}>
            {locale === 'zh' ? '尔湾学校指南' : 'Irvine Schools Guide'}
          </h1>
          <p style={{ fontSize: '1.15rem', opacity: 0.9, maxWidth: 700, margin: '0 auto' }}>
            {locale === 'zh'
              ? '为橙县华人家庭提供尔湾联合学区(IUSD)学校信息和资源'
              : 'School information and resources for Chinese-American families in the Irvine Unified School District (IUSD)'}
          </p>
        </div>
      </section>

      <section style={{ padding: 'var(--section-padding)', background: 'var(--gray-100)' }}>
        <div className="container" style={{ maxWidth: 900 }}>

          {/* High Schools */}
          <div style={sectionStyle}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--gray-900)', marginBottom: 20 }}>
              {locale === 'zh' ? '尔湾学区高中' : 'Irvine High Schools'}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {highSchools.map((s) => (
                <div key={s.name} style={schoolCardStyle}>
                  <div>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--gray-900)', marginBottom: 4 }}>{s.name}</h3>
                    {s.notes && <p style={{ fontSize: '0.88rem', color: 'var(--gray-500)', lineHeight: 1.5 }}>{s.notes}</p>}
                  </div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--blue-accent)', background: 'var(--blue-bg)', padding: '4px 10px', borderRadius: 20, whiteSpace: 'nowrap' }}>{s.type}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Middle Schools */}
          <div style={sectionStyle}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--gray-900)', marginBottom: 20 }}>
              {locale === 'zh' ? '尔湾学区初中' : 'Irvine Middle Schools'}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {middleSchools.map((s) => (
                <div key={s.name} style={schoolCardStyle}>
                  <div>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--gray-900)', marginBottom: 4 }}>{s.name}</h3>
                    {s.notes && <p style={{ fontSize: '0.88rem', color: 'var(--gray-500)', lineHeight: 1.5 }}>{s.notes}</p>}
                  </div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--green-accent)', background: 'var(--green-bg)', padding: '4px 10px', borderRadius: 20, whiteSpace: 'nowrap' }}>{s.type}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Elementary note */}
          <div style={sectionStyle}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--gray-900)', marginBottom: 20 }}>
              {locale === 'zh' ? '小学和学前班' : 'Elementary & Pre-School'}
            </h2>
            <div style={{ ...schoolCardStyle, flexDirection: 'column' }}>
              <p style={{ fontSize: '0.95rem', color: 'var(--gray-600)', lineHeight: 1.7 }}>
                {locale === 'zh'
                  ? 'IUSD拥有多所优秀的小学和学前班。我们提供小学入学指南、学校排名信息、以及幼儿园入学选择指导。如需了解特定学校的信息，请联系我们。'
                  : 'IUSD has many excellent elementary schools and preschools. We provide elementary school registration guides, school ranking information, and preschool enrollment guidance. Contact us for information about specific schools.'}
              </p>
            </div>
          </div>

          {/* Resources */}
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--gray-900)', marginBottom: 20 }}>
              {locale === 'zh' ? '教育资源' : 'Education Resources'}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
              {resources.map((r) => (
                <div key={r.title} className="card" style={{ padding: 20 }}>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--blue-accent)', marginBottom: 6 }}>{r.title}</h3>
                  <p style={{ fontSize: '0.85rem', color: 'var(--gray-600)', lineHeight: 1.5 }}>{r.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
